// CuestionarioModal — encuesta de feedback (4 preguntas) que se ofrece a
// usuarios de planes pagos con más de 2 semanas de antigüedad. Al completar,
// el usuario recibe +10 imágenes y la columna `cuestionario_respondido` de
// `company_profiles` queda en true para que el banner que dispara este modal
// no vuelva a aparecer. La lógica de visibilidad y el banner viven en
// `SeleccionarProductosAnuncio.tsx` (consumidor); este archivo solo
// implementa el flujo intro → form → submit.
//
// El modal tiene dos pantallas internas:
//   1. Intro: copy de invitación + botón "Responder" — explica el incentivo
//      antes de mostrar las preguntas para que el usuario decida con
//      contexto si vale la pena dedicar tiempo.
//   2. Form: las 4 preguntas en una sola vista scrolleable, con submit
//      al final. Se eligió pantalla única (no wizard paso-a-paso) para
//      que el usuario sienta que la encuesta es corta — cualquier
//      pantalla intermedia hace que parezca más larga de lo que es.

import React, { useState } from "react";
import { createPortal } from "react-dom";
import {
  X,
  Loader2,
  Sparkles,
  Check,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { supabase } from "../lib/supabase";
import { GENERATE_AUTH_TOKEN } from "../config";

// Webhook que acredita las +10 imágenes a la company. El cliente NO puede
// modificar `imagenes_restantes` directamente (RLS), así que delegamos en
// el backend pasando el id_company. El webhook es responsable de sumar el
// crédito; el cliente solo se ocupa de:
//   1. Insertar la fila en `cuestionarios` (con los datos de la encuesta).
//   2. Marcar `cuestionario_respondido = true` en `company_profiles`.
//   3. Llamar a este webhook para que se acrediten las imágenes.
const REWARD_WEBHOOK_URL =
  "https://ai-agency-dt7d.onrender.com/webhook/4a7bff42-ee96-4579-9362-068f8567f608";

// Opciones de la primera pregunta. El último elemento es un sentinel para
// que el usuario pueda escribir su propia descripción si ninguna encaja.
const DESCRIPCION_OPCIONES = [
  "Creación de contenido con IA",
  "Marketing con IA",
  "Vender más con IA",
  "Asistente para redes sociales",
  "Estudio de contenido con IA",
];

const USOS_OPCIONES: Array<{ id: string; label: string }> = [
  { id: "fotos_redes", label: "Fotos para las redes" },
  { id: "fotos_tienda", label: "Fotos para la tienda online" },
  { id: "videos_redes", label: "Videos para las redes" },
];

const FREE_IMAGES_REWARD = 10;

interface CuestionarioModalProps {
  open: boolean;
  idCompany: string;
  onClose: () => void;
  /**
   * Llamado cuando el envío del cuestionario fue exitoso. Recibe la cantidad
   * de imágenes acreditadas para que el caller actualice su estado local
   * (e.g. badge de imágenes restantes) sin tener que refetchear.
   */
  onSubmitted: (imagesCredited: number) => void;
}

const CuestionarioModal: React.FC<CuestionarioModalProps> = ({
  open,
  idCompany,
  onClose,
  onSubmitted,
}) => {
  // Pantalla actual: intro (copy + CTA) o form (preguntas).
  const [phase, setPhase] = useState<"intro" | "form" | "success">("intro");
  // Paso actual dentro de la fase form: 0..3 (4 preguntas, una por paso).
  // Mostrar de a una hace que la encuesta se sienta más liviana y dirigida
  // que un formulario largo donde se ven todas las preguntas a la vez.
  const TOTAL_STEPS = 4;
  const [step, setStep] = useState(0);
  // Q1 — descripción: el usuario puede elegir una predefinida (string del
  // array) o escribir libre (`customMode = true`). En ambos casos
  // `descripcionAtlas` lleva el texto que se manda a la DB.
  const [descripcionSelected, setDescripcionSelected] = useState<string | null>(
    null,
  );
  const [descripcionCustom, setDescripcionCustom] = useState("");
  const [descripcionCustomMode, setDescripcionCustomMode] = useState(false);
  // Q2 — checklist de usos.
  const [usosSelected, setUsosSelected] = useState<string[]>([]);
  // Q3 / Q4 — texto libre.
  const [funcionDeseada, setFuncionDeseada] = useState("");
  const [mejoraSugerida, setMejoraSugerida] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  if (!open) return null;

  const toggleUso = (id: string) => {
    setUsosSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  // Resolves to whatever descripción text we actually want stored: the
  // selected predefined option OR the custom text, depending on mode.
  const resolvedDescripcion = (): string | null => {
    if (descripcionCustomMode) {
      const trimmed = descripcionCustom.trim();
      return trimmed.length > 0 ? trimmed : null;
    }
    return descripcionSelected;
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      // 1. Insert the answer row. RLS ensures id_company matches the
      //    caller's profile so we don't have to double-check ownership here.
      const { error: insertErr } = await supabase
        .from("cuestionarios")
        .insert({
          id_company: idCompany,
          descripcion_atlas: resolvedDescripcion(),
          usos_atlas: usosSelected,
          funcion_deseada: funcionDeseada.trim() || null,
          mejora_sugerida: mejoraSugerida.trim() || null,
        });
      if (insertErr) throw insertErr;

      // 2. Disparar el webhook de premio. El backend se encarga de:
      //    - Sumar +10 imágenes a `imagenes_restantes`
      //    - Marcar `cuestionario_respondido = true` en `company_profiles`
      //    El cliente NO toca esos campos directamente. Hacemos await para
      //    que el UI de éxito no se muestre antes de que el premio esté
      //    en curso, pero no bloqueamos si el webhook falla — la fila de
      //    respuesta ya quedó guardada y el equipo puede acreditar
      //    manualmente si hace falta.
      try {
        await fetch(REWARD_WEBHOOK_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: GENERATE_AUTH_TOKEN,
          },
          body: JSON.stringify({ id_company: idCompany }),
        });
      } catch (webhookErr) {
        console.warn(
          "[CuestionarioModal] Reward webhook fallo (encuesta ya guardada):",
          webhookErr,
        );
      }

      setPhase("success");
      onSubmitted(FREE_IMAGES_REWARD);
    } catch (err: any) {
      setSubmitError(
        err?.message ||
          "No pudimos guardar tu respuesta. Probá de nuevo en unos segundos.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ── Intro screen ──────────────────────────────────────────────────────
  // En mobile (modal full-screen) centramos verticalmente el contenido —
  // el `flex-1` del wrapper crece hasta llenar la altura disponible y el
  // `justify-center` empuja el bloque al centro. En desktop (`sm:block`)
  // se mantiene alineado al top, igual que el resto de los modales del
  // proyecto.
  const introContent = (
    <div className="flex-1 overflow-y-auto flex flex-col justify-center sm:block">
      <div className="px-6 pt-2 pb-4">
        <div className="w-12 h-12 rounded-full bg-brand-primary/10 flex items-center justify-center mb-4 mx-auto sm:mx-0">
          <Sparkles className="w-5 h-5 text-brand-primary" />
        </div>
        <h2 className="text-[19px] sm:text-[21px] font-semibold text-brand-black/90 mb-2 text-center sm:text-left">
          Nos encantaría conocer tu opinión
        </h2>
        <p className="text-[15px] text-[#3C3C43]/70 leading-relaxed text-center sm:text-left">
          En Estudio Atlas estamos a tu servicio, por lo que quisiéramos saber
          qué pensás de la plataforma para mejorarla todavía más.
        </p>
        <p className="text-[15px] text-[#3C3C43]/70 leading-relaxed text-center sm:text-left mt-3">
          Respondé esta breve encuesta y recibí{" "}
          <span className="font-semibold text-brand-primary">
            {FREE_IMAGES_REWARD} imágenes extra gratis
          </span>
          .
        </p>
      </div>
      <div className="px-6 pb-6 pt-2">
        <button
          type="button"
          onClick={() => setPhase("form")}
          className="w-full inline-flex items-center justify-center h-11 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md text-[15px] font-medium shadow-sm transition-colors"
        >
          Responder
        </button>
      </div>
    </div>
  );

  // ── Form screen (wizard paso-a-paso) ─────────────────────────────────
  // Solo Q1 es obligatoria; las demás se pueden saltar dejando vacío.
  // La idea es que el usuario sienta la encuesta más corta de lo que
  // realmente es viendo una pregunta a la vez.
  const isStepValid = (() => {
    if (step === 0) return !!resolvedDescripcion();
    return true; // Q2/Q3/Q4 son opcionales
  })();
  const isLastStep = step === TOTAL_STEPS - 1;
  const advance = () => {
    if (!isStepValid) return;
    if (isLastStep) {
      handleSubmit();
    } else {
      setStep((s) => s + 1);
    }
  };
  const goBack = () => {
    if (step > 0) setStep((s) => s - 1);
  };

  // Renderizado de cada paso. Cada uno comparte el mismo header (título
  // de pregunta) y el body cambia según el tipo de input.
  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <>
            <p className="text-[15px] font-medium text-brand-black/85 mb-3">
              ¿Cómo describirías a Atlas?
            </p>
            {/* Lista vertical de opciones — un botón full-width por línea
                para que sea más legible y consistente con Q2 (checklist).
                La última fila ("Otra…") expande un input libre. */}
            <div className="space-y-2">
              {DESCRIPCION_OPCIONES.map((opt) => {
                const selected =
                  !descripcionCustomMode && descripcionSelected === opt;
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => {
                      setDescripcionCustomMode(false);
                      setDescripcionSelected(opt);
                    }}
                    className={`w-full text-left px-3.5 py-2.5 rounded-md text-[15px] transition-colors border ${
                      selected
                        ? "border-brand-primary bg-brand-primary text-white"
                        : "bg-white text-brand-black/85 border-neutral-200 hover:border-brand-primary/40"
                    }`}
                  >
                    {opt}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => {
                  setDescripcionCustomMode(true);
                  setDescripcionSelected(null);
                }}
                className={`w-full text-left px-3.5 py-2.5 rounded-md text-[15px] transition-colors border ${
                  descripcionCustomMode
                    ? "border-brand-primary bg-brand-primary text-white"
                    : "bg-white text-brand-black/85 border-neutral-200 hover:border-brand-primary/40"
                }`}
              >
                Otra…
              </button>
            </div>
            {descripcionCustomMode && (
              <input
                type="text"
                value={descripcionCustom}
                onChange={(e) => setDescripcionCustom(e.target.value)}
                placeholder="Escribí tu propia descripción"
                className="mt-3 w-full h-10 px-3 border border-neutral-200 rounded-md text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary"
                autoFocus
              />
            )}
          </>
        );
      case 1:
        return (
          <>
            <p className="text-[15px] font-medium text-brand-black/85 mb-1">
              ¿Para qué usás Atlas?
            </p>
            <p className="text-[12.5px] text-[#3C3C43]/60 mb-3">
              Podés marcar más de una.
            </p>
            <div className="space-y-2">
              {USOS_OPCIONES.map((opt) => {
                const selected = usosSelected.includes(opt.id);
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => toggleUso(opt.id)}
                    className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-md border transition-colors text-left ${
                      selected
                        ? "border-brand-primary bg-brand-primary"
                        : "border-neutral-200 bg-white hover:border-brand-primary/40"
                    }`}
                  >
                    {/* El checkbox se invierte cuando la fila está seleccionada:
                        sobre fondo azul, queda como cuadrado blanco con check
                        azul para que se vea contra el bg. */}
                    <span
                      className={`w-5 h-5 rounded-[6px] inline-flex items-center justify-center shrink-0 border ${
                        selected
                          ? "bg-white border-white text-brand-primary"
                          : "border-neutral-300 bg-white"
                      }`}
                    >
                      {selected && <Check className="w-3.5 h-3.5" />}
                    </span>
                    <span
                      className={`text-[15px] ${
                        selected ? "text-white" : "text-brand-black/85"
                      }`}
                    >
                      {opt.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        );
      case 2:
        return (
          <>
            <label className="block text-[15px] font-medium text-brand-black/85 mb-3">
              ¿Qué función te gustaría que añadamos en Atlas?
            </label>
            <textarea
              value={funcionDeseada}
              onChange={(e) => setFuncionDeseada(e.target.value)}
              placeholder="Lo que se te ocurra (opcional)"
              rows={5}
              autoFocus
              className="w-full px-3 py-2.5 border border-neutral-200 rounded-md text-[15px] resize-none focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary"
            />
          </>
        );
      case 3:
        return (
          <>
            <label className="block text-[15px] font-medium text-brand-black/85 mb-3">
              ¿Qué no te convence del todo y querés que mejoremos en Atlas?
            </label>
            <textarea
              value={mejoraSugerida}
              onChange={(e) => setMejoraSugerida(e.target.value)}
              placeholder="Tu feedback nos ayuda a priorizar (opcional)"
              rows={5}
              autoFocus
              className="w-full px-3 py-2.5 border border-neutral-200 rounded-md text-[15px] resize-none focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary"
            />
          </>
        );
    }
  };

  const formContent = (
    <>
      {/* Body de la pregunta. Los progress dots viven en la top-bar global
          alineados con la X (ver más abajo) — acá solo va el contenido.
          En mobile centramos verticalmente para que con la pantalla full
          la pregunta no quede pegada arriba; en desktop se mantiene
          alineado al top porque el modal tiene altura limitada. Si el
          contenido excede la altura disponible (textareas largos, etc.),
          el `overflow-y-auto` patea adentro de un wrapper flex-shrink-0
          y `min-h-min` evita que el justify-center recorte el inicio. */}
      <div className="flex-1 overflow-y-auto px-5 sm:px-6 pb-4 pt-3 flex flex-col justify-center sm:block">
        <div className="w-full">
          {renderStep()}
          {submitError && (
            <div className="mt-4 rounded-md bg-red-50 border border-red-100 px-3 py-2.5 text-[13.5px] text-red-700">
              {submitError}
            </div>
          )}
        </div>
      </div>
      <div className="px-5 sm:px-6 py-3 border-t border-black/[0.06] shrink-0 flex items-center gap-2">
        <button
          type="button"
          onClick={goBack}
          disabled={step === 0 || submitting}
          className={`inline-flex items-center justify-center gap-1 h-11 px-4 rounded-md text-[15px] font-medium transition-colors ${
            step === 0 || submitting
              ? "text-neutral-300 cursor-not-allowed"
              : "text-brand-black/70 hover:bg-neutral-100"
          }`}
        >
          <ChevronLeft className="w-4 h-4" />
          Atrás
        </button>
        <button
          type="button"
          onClick={advance}
          disabled={!isStepValid || submitting}
          className={`flex-1 inline-flex items-center justify-center gap-2 h-11 px-4 rounded-md text-[15px] font-medium shadow-sm transition-colors ${
            isStepValid && !submitting
              ? "bg-brand-primary hover:bg-brand-primary-hover text-white"
              : "bg-neutral-200 text-neutral-400 cursor-not-allowed"
          }`}
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Enviando…
            </>
          ) : isLastStep ? (
            `Enviar y obtener ${FREE_IMAGES_REWARD} imágenes`
          ) : (
            <>
              Siguiente
              <ChevronRight className="w-4 h-4" />
            </>
          )}
        </button>
      </div>
    </>
  );

  // ── Success screen ─────────────────────────────────────────────────────
  // Mismo patrón de centrado vertical que intro/form: en mobile el modal
  // es full-screen y el contenido queda centrado; en desktop alineado al
  // top como el resto de los modales.
  const successContent = (
    <div className="flex-1 overflow-y-auto flex flex-col justify-center sm:block">
      <div className="px-6 py-8 text-center">
        <div className="w-14 h-14 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-4">
          <Check className="w-6 h-6 text-success" />
        </div>
        <h2 className="text-[19px] font-semibold text-brand-black/90 mb-1.5">
          ¡Gracias por tu opinión!
        </h2>
        <p className="text-[15px] text-[#3C3C43]/70 mb-5">
          Te acreditamos{" "}
          <span className="font-semibold text-brand-primary">
            {FREE_IMAGES_REWARD} imágenes extra
          </span>
          .
        </p>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center justify-center h-11 px-6 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md text-[15px] font-medium shadow-sm transition-colors"
        >
          Listo
        </button>
      </div>
    </div>
  );

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-stretch sm:items-center justify-center bg-black/50 backdrop-blur-[2px] p-0 sm:p-4"
      onClick={() => {
        if (!submitting) onClose();
      }}
    >
      <div
        className="bg-white w-full sm:max-w-lg sm:rounded-[14px] shadow-2xl flex flex-col h-full sm:h-auto sm:max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top bar — durante el form muestra los progress dots a la
            izquierda alineados con la X. En intro/success solo la X.
            Los dots indican el paso activo (pill), completados (puntos
            llenos chicos) y pendientes (puntos vacíos chicos). */}
        <div className="flex items-center justify-between px-3 pt-3 shrink-0 gap-3">
          {phase === "form" ? (
            <div className="flex items-center gap-1.5 pl-2">
              {Array.from({ length: TOTAL_STEPS }).map((_, i) => {
                const isActive = i === step;
                const isCompleted = i < step;
                return (
                  <div
                    key={i}
                    className={`h-1.5 rounded-full transition-all ${
                      isActive
                        ? "w-8 bg-brand-primary"
                        : isCompleted
                          ? "w-1.5 bg-brand-primary"
                          : "w-1.5 bg-neutral-200"
                    }`}
                  />
                );
              })}
            </div>
          ) : (
            <div />
          )}
          <button
            type="button"
            onClick={() => {
              if (!submitting) onClose();
            }}
            className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors shrink-0"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {phase === "intro" && introContent}
        {phase === "form" && formContent}
        {phase === "success" && successContent}
      </div>
    </div>,
    document.body,
  );
};

export default CuestionarioModal;
